import DOMPurify, { type Config } from "dompurify";

/**
 * Sanitizzatore ad allow-list per il markup dei template e-mail (VULN-05).
 * Consente il sottoinsieme di formattazione/layout necessario a un template
 * (rich text, tabelle per il layout MJML/WYSIWYG, immagini, link) e rimuove
 * ogni vettore di script: <script>, handler on*, URL javascript:/vbscript:,
 * <iframe>, <object>, <embed>, <form>, <base>, <link>, <meta>.
 *
 * Usato sia lato import (Puck editor, MailEditor.tsx) sia lato export
 * (generazione dell'HTML finale in emailHtmlConverter.ts): entrambi i punti
 * devono restare allineati per evitare che un payload sopravviva in uno dei
 * due percorsi.
 */
const EMAIL_TEMPLATE_SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "html",
    "head",
    "body",
    "style",
    "title",
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "td",
    "th",
    "div",
    "span",
    "p",
    "br",
    "hr",
    "center",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "a",
    "img",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "sub",
    "sup",
    "ul",
    "ol",
    "li",
    "blockquote",
    "pre",
    "code",
    "small",
    "font",
  ],
  ALLOWED_ATTR: [
    "href",
    "src",
    "alt",
    "title",
    "width",
    "height",
    "align",
    "valign",
    "style",
    "class",
    "id",
    "bgcolor",
    "background",
    "color",
    "face",
    "size",
    "border",
    "cellpadding",
    "cellspacing",
    "colspan",
    "rowspan",
    "dir",
    "lang",
  ],
  ALLOWED_URI_REGEXP:
    /^(?:https?:|mailto:|cid:|data:image\/(?:png|jpe?g|gif|webp);base64,)/i,
  // Attributi il cui valore NON è mai un URL (colore, allineamento, misure...):
  // vanno esclusi dal controllo ALLOWED_URI_REGEXP sopra, altrimenti DOMPurify
  // li tratta come attributi "URI-bearing" e li scarta perché il loro valore
  // (es. "#fff", "center", "25") non soddisfa la regexp pensata per href/src.
  // "background" resta volutamente escluso da questa lista: può contenere un
  // URL (storicamente anche "javascript:") e deve restare soggetto al
  // controllo ALLOWED_URI_REGEXP.
  ADD_URI_SAFE_ATTR: [
    "color",
    "bgcolor",
    "face",
    "size",
    "border",
    "cellpadding",
    "cellspacing",
    "colspan",
    "rowspan",
    "align",
    "valign",
    "width",
    "height",
    "dir",
    "lang",
  ],
  FORBID_TAGS: [
    "script",
    "iframe",
    "object",
    "embed",
    "form",
    "input",
    "base",
    "link",
    "meta",
  ],
  FORBID_ATTR: ["srcdoc", "formaction", "ping"],
  ALLOW_DATA_ATTR: false,
  WHOLE_DOCUMENT: true,
} as const;

export function sanitizeEmailTemplate(html: string): string {
  if (!html) return html;
  return DOMPurify.sanitize(
    html,
    EMAIL_TEMPLATE_SANITIZE_CONFIG as unknown as Config,
  ) as unknown as string;
}
